import { Injectable } from "@angular/core";
import { HttpRequest, HttpHandler, HttpEvent, HttpInterceptor, HttpErrorResponse } from "@angular/common/http";
import { Observable, throwError, switchMap, catchError } from "rxjs";
import { Store } from "@ngxs/store";
import { Logout, RefreshToken } from "../auth/state/auth.action";
import { Router } from "@angular/router";
import { AuthService } from "../auth/service/auth.service";

@Injectable()
export class ErrorInterceptor implements HttpInterceptor {
  private isRefreshing = false;

  constructor(
    private store: Store,
    private router: Router,
    private authService: AuthService
  ) {}

  intercept(
    request: HttpRequest<unknown>,
    next: HttpHandler
  ): Observable<HttpEvent<unknown>> {
    return next.handle(request).pipe(
      catchError((error: HttpErrorResponse) => {
        // Não logar erros de validação silenciosa de streamers
        const isSilentValidation = error.url?.includes('/streamers?atname=') && 
                                  error.status === 400 &&
                                  request.headers.has('X-Silent-Validation');
        
        if (!isSilentValidation) {
          // Log do erro para debugging
          console.error("HTTP Error:", error);
        }

        // Se for erro 401 (Unauthorized) e não for uma tentativa de login ou refresh
        if (
          error.status === 401 &&
          !request.url.includes("/authorization/login") &&
          !request.url.includes("/authorization/refresh") &&
          !this.isRefreshing
        ) {
          // Verifica qual storage tem o token para manter a preferência do usuário
          const sessionToken = sessionStorage.getItem("accessToken");
          const localToken = localStorage.getItem("accessToken");
          const useSessionStorage = !!sessionToken;
          const currentToken = sessionToken || localToken;

          if (currentToken) {
            this.isRefreshing = true;

            // Tenta fazer refresh do token
            return this.authService.refreshToken().pipe(
              switchMap((refreshResponse) => {
                this.isRefreshing = false;

                // Atualiza o token no storage correto (mantém a preferência do usuário)
                if (useSessionStorage) {
                  sessionStorage.setItem(
                    "accessToken",
                    refreshResponse.accessToken
                  );
                  localStorage.removeItem("accessToken"); // Remove do outro storage
                } else {
                  localStorage.setItem(
                    "accessToken",
                    refreshResponse.accessToken
                  );
                  sessionStorage.removeItem("accessToken"); // Remove do outro storage
                }

                // Atualiza o token no store
                this.store.dispatch(
                  new RefreshToken(refreshResponse.accessToken)
                );

                // Retry da requisição original com o novo token
                const newRequest = request.clone({
                  setHeaders: {
                    Authorization: `Bearer ${refreshResponse.accessToken}`}});

                return next.handle(newRequest);
              }),
              catchError((refreshError) => {
                this.isRefreshing = false;

                // Remove tokens de ambos os storages
                sessionStorage.removeItem("accessToken");
                localStorage.removeItem("accessToken");

                // Faz logout e redireciona
                this.store.dispatch(new Logout());
                this.router.navigate(["/auth/login"]);
                return throwError(() => refreshError);
              })
            );
          } else {
            this.router.navigate(["/auth/login"]);
          }
        }

        // Se for erro 403 (Forbidden) com token válido
        if (
          error.status === 403 &&
          (localStorage.getItem("accessToken") ||
            sessionStorage.getItem("accessToken"))
        ) {
          this.store.dispatch(new Logout());
          this.router.navigate(["/auth/login"]);
        }

        return throwError(() => error);
      })
    );
  }
}
